import React from 'react';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { SECTIONS, questionNumber, isVisible } from './surveyQuestions';

// ---------------------------------------------------------------------------
// Read-only copy of a user's full survey response: every question with the
// answer they gave (or "Not answered"), grouped by section.
// ---------------------------------------------------------------------------

const NotAnswered = () => <span className="italic font-normal text-gray-400">Not answered</span>;

const optionLabel = (q, value, answers) => {
  const o = q.options.find((x) => x.value === value);
  if (!o) return String(value);
  return o.other ? `Other: ${answers[`${q.id}_other`] || '(not specified)'}` : o.label;
};

const Answer = ({ q, answers }) => {
  const v = answers[q.id];
  if (q.type === 'multi') {
    if (!Array.isArray(v) || !v.length) return <NotAnswered />;
    return (
      <ul className="list-disc pl-5 space-y-0.5">
        {v.map((x) => <li key={x}>{optionLabel(q, x, answers)}</li>)}
      </ul>
    );
  }
  if (q.type === 'single') return v ? optionLabel(q, v, answers) : <NotAnswered />;
  if (q.type === 'scale') return v ? q.scale[Number(v) - 1] : <NotAnswered />;
  return v && String(v).trim() ? <span className="whitespace-pre-wrap">{v}</span> : <NotAnswered />;
};

const Row = ({ label, text, children }) => (
  <div className="py-3 flex items-start gap-3">
    {label}
    <div className="min-w-0 flex-1">
      <p className="text-sm text-gray-600 leading-snug">{text}</p>
      <div className="text-sm font-medium text-gray-900 mt-1">{children}</div>
    </div>
  </div>
);

const NumberLabel = ({ n }) => (
  <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[2rem] h-6 px-1.5 rounded-md bg-primary-50 text-primary-700 text-xs font-semibold">
    Q{n}
  </span>
);

const TagLabel = ({ children }) => (
  <span className="flex-shrink-0 inline-flex items-center h-6 px-2 rounded-md bg-gray-100 text-gray-600 text-[11px] font-semibold uppercase tracking-wide">
    {children}
  </span>
);

// Section comment boxes are optional extras: list them only when filled in.
const isShown = (q, answers) =>
  isVisible(q, answers) && !(q.comment && !String(answers[q.id] || '').trim());

// `sectionIds` limits it to some sections (all by default); `onEditSection(i)`
// adds an Edit link per section, i being the section's index in SECTIONS.
const ResponseSummary = ({ answers, onEditSection, sectionIds }) => (
  <div className="space-y-4">
    {SECTIONS.map((s, i) => (!sectionIds || sectionIds.includes(s.id)) && (
      <section key={s.id} className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">
            <span className="text-gray-400 mr-1.5">{i + 1}.</span>
            {s.title}
          </h3>
          {onEditSection && (
            <button
              onClick={() => onEditSection(i)}
              className="inline-flex items-center text-xs font-medium text-primary-600 hover:text-primary-700"
            >
              <PencilSquareIcon className="h-4 w-4 mr-1" />
              Edit
            </button>
          )}
        </div>
        <div className="px-5 divide-y divide-gray-100">
          {s.pages.flatMap((p) => p.questions).filter((q) => isShown(q, answers)).map((q) =>
            q.type === 'statements' ? (
              <div key={q.id} className="py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400 mb-1">{q.text}</p>
                <div className="divide-y divide-gray-50">
                  {q.items.map((it) => (
                    <Row key={it.id} label={<NumberLabel n={questionNumber(it.id)} />} text={it.text}>
                      {answers[it.id] ? q.scale[Number(answers[it.id]) - 1] : <NotAnswered />}
                    </Row>
                  ))}
                </div>
              </div>
            ) : (
              <div key={q.id} className={q.followUp ? 'pl-6' : ''}>
                <Row
                  label={q.followUp ? <TagLabel>Follow-up</TagLabel> : q.comment ? <TagLabel>Comments</TagLabel> : <NumberLabel n={questionNumber(q.id)} />}
                  text={q.comment ? 'Your comments on this section' : q.text}
                >
                  <Answer q={q} answers={answers} />
                </Row>
              </div>
            )
          )}
        </div>
      </section>
    ))}
  </div>
);

export default ResponseSummary;
